import type { FeatureEvents, ScreenEvent, TrackEvent, UIEvent } from '../new-analytics-types';
import { Action, ActionSubject, TrackAction, TrackActionSubject } from '../new-analytics-types';

type ScreenKeys =
  | 'ipAllowlistScreenEvent'
  | 'ipAllowlistCreateModalScreenEvent'
  | 'ipAllowlistEditModalScreenEvent'
  | 'ipAllowlistDeleteModalScreenEvent'
  | 'ipAllowlistDetailsScreenEvent';
type UIEventKeys =
  | 'createClickEvent'
  | 'learnMoreClickEvent'
  | 'cancelCreateClickEvent'
  | 'createConfirmClickEvent'
  | 'switchStatusToggleEvent'
  | 'editClickEvent'
  | 'saveClickEvent'
  | 'cancelEditClickEvent'
  | 'deleteClickEvent'
  | 'deleteConfirmClickEvent'
  | 'cancelDeleteClickEvent'
  | 'viewMobileAppPoliciesEvent'
  | 'toggleIpAllowlistClickEvent'
  | 'showIpAllowlistDetailsPageClickEvent'
  | 'confirmToggleIpAllowlistClickEvent'
  | 'cancelToggleIpAllowlistClickEvent'
  | 'addIpAllowlistItemClickEvent'
  | 'confirmAddIpAllowlistItemClickEvent'
  | 'cancelAddIpAllowlistItemClickEvent'
  | 'deleteIpAllowlistItemClickEvent'
  | 'confirmDeleteIpAllowlistItemClickEvent'
  | 'cancelDeleteIpAllowlistItemClickEvent';
type TrackEventKeys = 'createdIpAllowlist' | 'deletedIpAllowlist' | 'changedIpAllowlist' | 'enabledAllowList' | 'disabledAllowList';

export const ipAllowlistScreen = 'ipAllowlistScreen';
export const ipAllowlistCreateModalScreen = 'ipAllowlistCreateModalScreen';
export const ipAllowlistEditModalScreen = 'ipAllowlistEditModalScreen';
export const ipAllowlistDeleteModalScreen = 'ipAllowlistDeleteModalScreen';
export const ipAllowlistDetailsScreen = 'ipAllowlistDetailsScreen';

export const ipAllowlistEvents: FeatureEvents<ScreenKeys, UIEventKeys, TrackEventKeys, string> = {
  confluenceSpec: 'https://hello.atlassian.net/wiki/spaces/AtlasPrime/pages/629947870/IP+Allowlisting+Front+end+Analytics+Spec',
  screen: {
    ipAllowlistScreenEvent: (attributes: {
      premiumProductsCount: number;
      allowListCount: number;
      allowMobileBypass: boolean;
      mobileAppPolicyCount: number;
    }): ScreenEvent => ({
      data: {
        name: ipAllowlistScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/26474',
    }),
    ipAllowlistCreateModalScreenEvent: (attributes: {
      premiumProductsCount: number;
      allowListCount: number;
      allowMobileBypass: boolean;
      mobileAppPolicyCount: number;
    }): ScreenEvent => ({
      data: {
        name: ipAllowlistCreateModalScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/26475',
    }),
    ipAllowlistEditModalScreenEvent: (attributes: {
      premiumProductsCount: number;
      allowListCount: number;
      allowMobileBypass: boolean;
      mobileAppPolicyCount: number;
      policyId?: string;
      ipAddressesCount: number;
      enabled: string;
    }): ScreenEvent => ({
      data: {
        name: ipAllowlistEditModalScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/26476',
    }),
    ipAllowlistDeleteModalScreenEvent: (attributes: {
      premiumProductsCount: number;
      allowListCount: number;
      allowMobileBypass: boolean;
      mobileAppPolicyCount: number;
      policyId?: string;
      ipAddressesCount: number;
      enabled: string;
    }): ScreenEvent => ({
      data: {
        name: ipAllowlistDeleteModalScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/26477',
    }),
    ipAllowlistDetailsScreenEvent: (attributes: { policyId: string; enabled: boolean; ruleType: 'ip' | 'country' }): ScreenEvent => ({
      data: {
        name: ipAllowlistDetailsScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/99928',
    }),
  },
  ui: {
    createClickEvent: (attributes: { allowListCount: number; allowMobileBypass: boolean; mobileAppPolicyCount: number }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'createAllowList',
        actionSubject: ActionSubject.Button,
        source: ipAllowlistScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/26489',
    }),
    learnMoreClickEvent: (attributes: { hasAccessSubscription: boolean }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'learnMore',
        actionSubject: ActionSubject.Button,
        source: ipAllowlistScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/22168',
    }),
    cancelCreateClickEvent: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'cancel',
        actionSubject: ActionSubject.Button,
        source: ipAllowlistCreateModalScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/15990',
    }),
    createConfirmClickEvent: (attributes: {
      productsAppliedCount: number;
      ipAddressesCount: number;
      enabled: boolean;
      allowMobileBypass: boolean;
      mobileAppPolicyCount: number;
    }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'createAllowlist',
        actionSubject: ActionSubject.Button,
        source: ipAllowlistCreateModalScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/26488',
    }),
    switchStatusToggleEvent: (attributes: { statusChanged: 'enabled' | 'disabled' }): UIEvent => ({
      data: {
        action: Action.Switched,
        actionSubjectId: 'enabledDisableAllowList',
        actionSubject: ActionSubject.Toggle,
        source: ipAllowlistScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/26487',
    }),
    editClickEvent: (attributes: { enabled: boolean }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'editAllowList',
        actionSubject: ActionSubject.Button,
        source: ipAllowlistScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/26486',
    }),
    saveClickEvent: (attributes: {
      productsAppliedCount: number;
      ipAddressesCount: number;
      allowMobileBypass: boolean;
      mobileAppPolicyCount: number;
      hasNameBeenChanged: boolean;
      policyId: string;
      enabled: string;
    }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'save',
        actionSubject: ActionSubject.Button,
        source: ipAllowlistEditModalScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/26485',
    }),
    cancelEditClickEvent: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'cancel',
        actionSubject: ActionSubject.Button,
        source: ipAllowlistEditModalScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/15990',
    }),
    deleteClickEvent: (attributes: { enabled: boolean }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'delete',
        actionSubject: ActionSubject.Button,
        source: ipAllowlistScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/26484',
    }),
    deleteConfirmClickEvent: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'confirm',
        actionSubject: ActionSubject.Button,
        source: ipAllowlistDeleteModalScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/25543',
    }),
    cancelDeleteClickEvent: (): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'cancel',
        actionSubject: ActionSubject.Button,
        source: ipAllowlistDeleteModalScreen,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/15990',
    }),
    viewMobileAppPoliciesEvent: (attributes: { ipAllowlistCount: number }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'viewMobileAppPolicies',
        actionSubject: ActionSubject.Link,
        source: ipAllowlistScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/74464',
    }),
    toggleIpAllowlistClickEvent: (attributes: { enabled: boolean; source: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'toggleIpAllowlist',
        actionSubject: ActionSubject.Button,
        source: attributes.source,
        attributes: { enabled: attributes.enabled },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/99954',
    }),
    showIpAllowlistDetailsPageClickEvent: (attributes: { policyId: string; enabled: boolean; ruleType: 'ip' | 'country' }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'showIpAllowlistDetailsPage',
        actionSubject: ActionSubject.Button,
        source: ipAllowlistScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/99955',
    }),
    confirmToggleIpAllowlistClickEvent: (attributes: { enabled: boolean; source: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'confirmToggleIpAllowlist',
        actionSubject: ActionSubject.Button,
        source: attributes.source,
        attributes: { enabled: attributes.enabled },
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/99956',
    }),
    cancelToggleIpAllowlistClickEvent: (attributes: { source: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'cancelToggleIpAllowlist',
        actionSubject: ActionSubject.Button,
        source: attributes.source,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/99957',
    }),
    addIpAllowlistItemClickEvent: (attributes: { type: 'ip' | 'country' | 'app'; policyId: string }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'addIpAllowlistItem',
        actionSubject: ActionSubject.Button,
        source: ipAllowlistDetailsScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/99958',
    }),
    confirmAddIpAllowlistItemClickEvent: (attributes: { type: 'ip' | 'country' | 'app'; policyId: string; count: number }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'confirmAddIpAllowlistItem',
        actionSubject: ActionSubject.Button,
        source: ipAllowlistDetailsScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/99959',
    }),
    cancelAddIpAllowlistItemClickEvent: (attributes: { type: 'ip' | 'country' | 'app' }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'cancelAddIpAllowlistItem',
        actionSubject: ActionSubject.Button,
        source: ipAllowlistDetailsScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/99960',
    }),
    deleteIpAllowlistItemClickEvent: (attributes: { type: 'ip' | 'country' | 'app'; policyId: string; bulk: boolean }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'deleteIpAllowlistItem',
        actionSubject: ActionSubject.Button,
        source: ipAllowlistDetailsScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/99961',
    }),
    confirmDeleteIpAllowlistItemClickEvent: (attributes: {
      type: 'ip' | 'country' | 'app';
      policyId: string;
      bulk: boolean;
      count: number;
    }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'confirmDeleteIpAllowlistItem',
        actionSubject: ActionSubject.Button,
        source: ipAllowlistDetailsScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/99962',
    }),
    cancelDeleteIpAllowlistItemClickEvent: (attributes: { type: 'ip' | 'country' | 'app' }): UIEvent => ({
      data: {
        action: Action.Clicked,
        actionSubjectId: 'cancelDeleteIpAllowlistItem',
        actionSubject: ActionSubject.Button,
        source: ipAllowlistDetailsScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/99963',
    }),
  },
  track: {
    createdIpAllowlist: (attributes: {
      enabled: boolean;
      selectedProducts: string[];
      ipAddressesCount: number;
      allowMobileBypass: boolean;
      policyId: string;
      mobileAppPolicyCount: number;
      countriesCount?: number;
      ruleType?: 'ip' | 'country';
    }): TrackEvent => ({
      data: {
        action: TrackAction.Created,
        actionSubject: TrackActionSubject.IpAllowlist,
        source: ipAllowlistScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/26478',
    }),
    deletedIpAllowlist: (attributes: {
      enabled: boolean;
      selectedProducts: string[];
      ipAddressesCount: number;
      allowMobileBypass: boolean;
      mobileAppPolicyCount: number;
      policyId: string;
      countriesCount?: number;
      ruleType?: 'ip' | 'country';
    }): TrackEvent => ({
      data: {
        action: TrackAction.Deleted,
        actionSubject: TrackActionSubject.IpAllowlist,
        source: ipAllowlistDeleteModalScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/26479',
    }),
    changedIpAllowlist: (attributes: {
      enabled: boolean;
      selectedProducts: string[];
      ipAddressesCount: number;
      allowMobileBypass: boolean;
      policyId: string;
      mobileAppPolicyCount: number;
      hasNameBeenChanged: boolean;
      countriesCount?: number;
      ruleType?: 'ip' | 'country';
    }): TrackEvent => ({
      data: {
        action: TrackAction.Changed,
        actionSubject: TrackActionSubject.IpAllowlist,
        source: ipAllowlistEditModalScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/26480',
    }),
    enabledAllowList: (attributes: {
      selectedProducts: string[];
      ipAddressesCount: number;
      allowMobileBypass: boolean;
      mobileAppPolicyCount: number;
      policyId: string;
      countriesCount?: number;
      ruleType?: 'ip' | 'country';
    }): TrackEvent => ({
      data: {
        action: TrackAction.Enabled,
        actionSubject: TrackActionSubject.IpAllowlist,
        source: ipAllowlistScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/26481',
    }),
    disabledAllowList: (attributes: {
      selectedProducts: string[];
      ipAddressesCount: number;
      allowMobileBypass: boolean;
      mobileAppPolicyCount: number;
      policyId: string;
      countriesCount?: number;
      ruleType?: 'ip' | 'country';
    }): TrackEvent => ({
      data: {
        action: TrackAction.Disabled,
        actionSubject: TrackActionSubject.IpAllowlist,
        source: ipAllowlistScreen,
        attributes,
      },
      dataPortalLink: 'https://data-portal.internal.atlassian.com/analytics/registry/26482',
    }),
  },
  operational: {},
};
